// OCR Service using Google Cloud Vision API
const VISION_API_KEY = 'AIzaSyD819yl6aZ_wna_OZjqFq-oLeVhEN8a5jI';
const VISION_API_URL = `https://vision.googleapis.com/v1/images:annotate?key=${VISION_API_KEY}`;

/**
 * Extract text from a receipt image using Google Cloud Vision API
 * @param {File} imageFile - The image file to process
 * @returns {Promise<Object>} - Extracted receipt data
 */
export const scanReceipt = async (imageFile) => {
  try {
    // Convert image to base64
    const base64Image = await fileToBase64(imageFile);
    
    // Remove data URL prefix (data:image/jpeg;base64,)
    const base64Content = base64Image.split(',')[1];

    console.log('Calling Vision API...');

    // Call Vision API
    const response = await fetch(VISION_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        requests: [
          {
            image: {
              content: base64Content
            },
            features: [
              {
                type: 'DOCUMENT_TEXT_DETECTION',
                maxResults: 1
              }
            ]
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Vision API Error:', error);
      throw new Error(`Vision API error: ${JSON.stringify(error)}`);
    }

    const data = await response.json();
    console.log('Vision API Response:', data);
    
    // Extract text from response
    const detections = data.responses[0];
    
    if (!detections.textAnnotations || detections.textAnnotations.length === 0) {
      throw new Error('No text found in image');
    }

    const fullText = detections.textAnnotations[0].description;
    console.log('Extracted Text:', fullText);
    
    // Parse the receipt data
    const receiptData = parseReceiptText(fullText);
    console.log('Parsed Receipt Data:', receiptData);
    
    return {
      success: true,
      data: receiptData,
      rawText: fullText
    };

  } catch (error) {
    console.error('OCR Error:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Parse extracted text to find receipt information
 * @param {string} text - Raw text from OCR
 * @returns {Object} - Parsed receipt data
 */
const parseReceiptText = (text) => {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  console.log('Parsing lines:', lines);
  
  // Initialize receipt data
  const receiptData = {
    merchant: '',
    amount: 0,
    date: '',
    items: [],
    tax: 0,
    category: 'Other',
    taxDeductible: false
  };

  // Extract merchant (usually first 1-3 lines)
  receiptData.merchant = extractMerchant(lines);
  console.log('Merchant:', receiptData.merchant);

  // Extract total amount
  receiptData.amount = extractTotal(lines);
  console.log('Amount:', receiptData.amount);

  // Extract date
  receiptData.date = extractDate(lines);
  console.log('Date:', receiptData.date);

  // Extract tax
  receiptData.tax = extractTax(lines);
  console.log('Tax:', receiptData.tax);

  // Auto-categorize based on merchant
  receiptData.category = categorizeMerchant(receiptData.merchant);
  console.log('Category:', receiptData.category);

  // Determine if tax deductible
  receiptData.taxDeductible = isBusinessExpense(receiptData.category, receiptData.merchant);
  console.log('Tax Deductible:', receiptData.taxDeductible);

  return receiptData;
};

/**
 * Extract merchant name from receipt lines
 */
const extractMerchant = (lines) => {
  // Common receipt keywords to skip
  const skipWords = ['receipt', 'thank you', 'thanks', 'total', 'subtotal', 'tax', 'date', 'time', 'store', 'number'];
  
  // Look through first 8 lines for merchant name
  for (let i = 0; i < Math.min(8, lines.length); i++) {
    const line = lines[i].toLowerCase();
    
    // Skip lines that are just numbers or dates
    if (/^\d+$/.test(line)) continue;
    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(line)) continue;
    
    // Skip lines with common receipt words
    if (skipWords.some(word => line === word)) continue;
    
    // Skip very short lines
    if (line.length < 3) continue;
    
    // If line has letters and is reasonable length, it's probably the merchant
    if (/[a-z]/i.test(line) && line.length >= 3 && line.length < 50) {
      // Clean up the merchant name
      let merchant = lines[i]
        .replace(/[^a-zA-Z0-9\s&'-]/g, '') // Remove special chars except &, ', -
        .trim();
      
      if (merchant.length >= 3) {
        return merchant;
      }
    }
  }
  
  return lines[0] || 'Unknown Merchant';
};

/**
 * Extract total amount from receipt
 */
const extractTotal = (lines) => {
  const totalKeywords = ['total', 'amount due', 'balance', 'charge', 'grand total', 'amount'];
  let highestAmount = 0;
  let foundTotal = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();
    
    // Look for lines with "total" or similar keywords
    const hasKeyword = totalKeywords.some(keyword => lowerLine.includes(keyword));
    
    // Extract all numbers that look like prices (with decimal points)
    const priceMatches = line.match(/\$?\s*\d+[,.]?\d*\.?\d{2}/g);
    
    if (priceMatches) {
      for (const match of priceMatches) {
        // Clean the match and convert to number
        const cleanMatch = match.replace(/[$,\s]/g, '');
        const amount = parseFloat(cleanMatch);
        
        // If this line has a total keyword, prioritize it
        if (hasKeyword && amount > 0) {
          // Make sure it's not a subtotal by checking next few lines
          const isLikelyFinalTotal = !lowerLine.includes('subtotal') && 
                                     !lowerLine.includes('sub total') &&
                                     !lowerLine.includes('sub-total');
          
          if (isLikelyFinalTotal && (!foundTotal || amount > foundTotal)) {
            foundTotal = amount;
          }
        }
        
        // Track highest amount as backup
        if (amount > highestAmount && amount < 10000) { // Sanity check
          highestAmount = amount;
        }
      }
    }
  }

  return foundTotal || highestAmount;
};

/**
 * Extract date from receipt
 */
const extractDate = (lines) => {
  const today = new Date().toISOString().split('T')[0];

  for (const line of lines) {
    // MM/DD/YYYY or MM/DD/YY
    let match = line.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (match) {
      let [_, month, day, year] = match;
      if (year.length === 2) {
        year = '20' + year;
      }
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // YYYY-MM-DD
    match = line.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return match[0];
    }

    // DD-MM-YYYY or DD/MM/YYYY
    match = line.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
    if (match) {
      let [_, day, month, year] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  // Default to today if no date found
  return today;
};

/**
 * Extract tax amount from receipt
 */
const extractTax = (lines) => {
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    
    if (lowerLine.includes('tax') && !lowerLine.includes('total')) {
      // Extract price from this line
      const priceMatch = line.match(/\$?\s*\d+[,.]?\d*\.?\d{2}/);
      if (priceMatch) {
        const cleanMatch = priceMatch[0].replace(/[$,\s]/g, '');
        return parseFloat(cleanMatch);
      }
    }
  }

  return 0;
};

/**
 * Categorize based on merchant name - COMPREHENSIVE & IRS COMPLIANT
 * Updated with hundreds of keywords for accurate categorization
 */
const categorizeMerchant = (merchant) => {
  const merchantLower = merchant.toLowerCase();

  // ==================== COMPUTER & SOFTWARE (IRS Section 179) ====================
  // IRS allows 100% deduction for business computers, software, and technology
  // Section 179 for equipment, regular deduction for software subscriptions
  if (merchantLower.match(/apple|dell|hp|lenovo|asus|acer|msi|razer|alienware|microsoft surface|macbook|imac|ipad|tablet|monitor|keyboard|mouse|webcam|microphone|headset|speaker|printer|scanner|router|modem|hard drive|ssd|ram|memory|graphics card|gpu|processor|cpu|motherboard|best buy|micro center|newegg|b&h photo|tiger direct|adobe|microsoft 365|office 365|windows|creative cloud|photoshop|slack|zoom|teams|notion|evernote|trello|asana|monday|clickup|airtable|dropbox|google workspace|gsuite|icloud.*storage|onedrive|box\.com|github|gitlab|bitbucket|jira|confluence|shopify|wix|squarespace|wordpress|webflow|quickbooks|xero|freshbooks|wave|sage|salesforce|hubspot|pipedrive|zoho|canva|figma|sketch|invision|aws|amazon web services|azure|google cloud|digitalocean|linode|vultr|heroku|netlify|vercel|godaddy|namecheap|bluehost|hostgator|siteground|jetbrains|visual studio|sublime|postman|insomnia|datadog|new relic|sentry|splunk|chatgpt|openai|anthropic|claude|midjourney|jasper|copy\.ai|grammarly|hemingway|norton|mcafee|kaspersky|bitdefender|malwarebytes|vpn|nordvpn|expressvpn|discord nitro|telegram premium|whatsapp business|software|saas|cloud.*service|digital.*tool|app.*subscription|tech.*subscription|computer.*repair|it.*service|tech.*support/)) {
    return 'Computer & Software';
  }

  // ==================== OFFICE SUPPLIES (100% Deductible) ====================
  if (merchantLower.match(/staples|office depot|officemax|office supply|paper|envelope|folder|binder|notepad|notebook|journal|planner|pen|pencil|marker|highlighter|sharpie|clipboard|stapler|tape|glue|scissors|ruler|calculator|file cabinet|filing|organizer|label|office.*supplies|desk.*supplies|stationery/)) {
    return 'Office Supplies';
  }

  // ==================== MEALS & ENTERTAINMENT (50% Deductible) ====================
  if (merchantLower.match(/mcdonald|burger king|wendy|taco bell|kfc|popeyes|chick-fil-a|subway|arby|sonic|jack in the box|in-n-out|five guys|shake shack|chipotle|qdoba|panda express|starbucks|dunkin|dutch bros|peet|caribou|coffee bean|local.*coffee|cafe|pizza hut|domino|papa john|little caesars|marco.*pizza|round table|applebee|chili|olive garden|red lobster|outback|texas roadhouse|longhorn|cheesecake factory|bj.*restaurant|buffalo wild|panera|blaze pizza|mod pizza|sweetgreen|cava|restaurant|diner|bistro|grille?|eatery|cafeteria|cantina|tavern|pub|bar.*grill|kitchen|steakhouse|seafood|sushi|bbq|bakery|deli|sandwich|burrito|noodle|ramen|pho|breakfast|brunch|lunch|dinner|food.*delivery|doordash|uber eats|grubhub|postmates|delivery.*service|catering|food.*service/)) {
    return 'Meals & Entertainment';
  }

  // ==================== TRANSPORTATION (IRS Standard Mileage or Actual) ====================
  if (merchantLower.match(/shell|chevron|exxon|mobil|bp|76|arco|texaco|conoco|phillips 66|sunoco|marathon|valero|citgo|speedway|wawa|sheetz|gas.*station|fuel|petrol|gas & go|pump|oil change|jiffy lube|valvoline|pep boys|autozone|o'reilly|napa|car wash|car.*care|auto.*repair|mechanic|tire|brake|parking|park.*fee|toll|ezpass|fastrak|uber|lyft|taxi|cab|ride.*share|dmv|registration|smog.*check|vehicle.*inspection|metro|subway|bus.*pass|transit|train|rail|amtrak|enterprise|hertz|budget|avis|national|alamo|thrifty|dollar.*rent/)) {
    return 'Transportation';
  }

  // ==================== TRAVEL & LODGING (100% Deductible for Business) ====================
  if (merchantLower.match(/marriott|hilton|hyatt|sheraton|westin|holiday inn|hampton|courtyard|residence inn|fairfield|homewood suites|embassy suites|doubletree|radisson|ramada|best western|comfort inn|quality inn|la quinta|motel 6|super 8|days inn|red roof|airbnb|vrbo|booking\.com|hotels\.com|expedia|priceline|trivago|united|american airlines|delta|southwest|jetblue|alaska|frontier|spirit|allegiant|hawaiian|tsa pre|global entry|clear|airport|airline|flight|airfare|travel.*agency|cruise|hotel|motel|inn|lodge|resort|hostel|bed.*breakfast|b&b/)) {
    return 'Travel & Lodging';
  }

  // ==================== PROFESSIONAL SERVICES (100% Deductible) ====================
  if (merchantLower.match(/attorney|lawyer|legal.*service|law.*firm|legal.*aid|notary|accountant|cpa|bookkeeper|bookkeeping|tax.*prep|tax.*service|h&r block|turbotax.*live|consultant|consulting|advisor|advisory|professional.*service|business.*service|contractor|freelance|architect|engineer|surveyor|appraiser|inspector/)) {
    return 'Professional Services';
  }

  // ==================== MARKETING & ADVERTISING (100% Deductible) ====================
  if (merchantLower.match(/google ads|google.*adwords|facebook ads|meta.*ads|instagram.*ads|linkedin.*ads|twitter.*ads|tiktok.*ads|snapchat.*ads|pinterest.*ads|youtube.*ads|reddit.*ads|mailchimp|constant contact|hubspot|salesforce.*marketing|hootsuite|buffer|sprout social|later|semrush|ahrefs|moz|fiverr|upwork|99designs|designcrowd|billboard|radio.*ad|tv.*ad|print.*ad|newspaper.*ad|magazine.*ad|marketing|advertising|promotion|promo|campaign|seo|sem|ppc|social media.*marketing|content.*marketing|email.*marketing|brand|publicity/)) {
    return 'Marketing & Advertising';
  }

  // ==================== UTILITIES (100% Deductible - Home Office) ====================
  if (merchantLower.match(/pg&e|pge|sce|southern california edison|sdge|con.*edison|duke energy|florida power|georgia power|electric|power.*company|energy.*company|comcast|xfinity|spectrum|cox|att|verizon|t-mobile|sprint|centurylink|frontier|optimum|charter|internet|broadband|wifi|fiber|cable|phone.*service|wireless|cellular|mobile.*service|water|sewer|gas.*company|waste.*management|trash|recycling|utility|utilities/)) {
    return 'Utilities';
  }

  // ==================== EQUIPMENT (Section 179 - 100% Deductible) ====================
  if (merchantLower.match(/desk|chair|table|filing cabinet|bookshelf|shelving|office.*furniture|standing desk|ergonomic|equipment|machinery|tool|apparatus|projector|display|television|tv|screen|camera|dslr|gopro|drone|phone.*system|fax|copier|pos|point of sale|cash register|credit card.*terminal|square.*reader|generator|compressor|safe|security.*system|alarm/)) {
    return 'Equipment';
  }

  // ==================== INSURANCE (100% Deductible) ====================
  if (merchantLower.match(/business.*insurance|liability.*insurance|professional.*liability|general.*liability|commercial.*insurance|workers.*comp|health.*insurance|dental.*insurance|vision.*insurance|insurance.*premium|policy.*payment/)) {
    return 'Insurance';
  }

  // ==================== EDUCATION & TRAINING (100% Deductible) ====================
  if (merchantLower.match(/udemy|coursera|linkedin.*learning|skillshare|pluralsight|masterclass|udacity|edx|khan academy|seminar|workshop|conference|training|certification|course|webinar|continuing.*education|professional.*development|amazon.*books|barnes.*noble|book|publication|magazine.*subscription|journal.*subscription|trade.*publication/)) {
    return 'Education & Training';
  }

  // ==================== SHIPPING & POSTAGE (100% Deductible) ====================
  if (merchantLower.match(/usps|fedex|ups|dhl|postal.*service|shipping|postage|freight|courier|stamps|mail.*service/)) {
    return 'Shipping & Postage';
  }

  return 'Other';
};

/**
 * Determine if expense is likely business-related and tax deductible
 * Updated to include new categories
 */
const isBusinessExpense = (category, merchant) => {
  const merchantLower = merchant.toLowerCase();
  
  // Always 100% deductible categories (IRS compliant)
  const alwaysDeductible = [
    'Computer & Software',      // NEW! Section 179 + software subscriptions
    'Office Supplies',
    'Professional Services',
    'Marketing & Advertising',
    'Utilities',
    'Equipment',
    'Insurance',
    'Education & Training',
    'Shipping & Postage'
  ];

  if (alwaysDeductible.includes(category)) {
    return true;
  }

  // Meals are 50% deductible if business-related
  if (category === 'Meals & Entertainment') {
    return true;
  }

  // Travel is usually 100% deductible for business
  if (category === 'Travel & Lodging') {
    return true;
  }

  // Transportation/Gas is deductible (actual expenses or standard mileage)
  if (category === 'Transportation') {
    return true;
  }

  // Check merchant name for business indicators
  const businessIndicators = ['business', 'office', 'professional', 'corporate', 'commercial', 'enterprise'];
  if (businessIndicators.some(indicator => merchantLower.includes(indicator))) {
    return true;
  }

  // Default to not deductible for 'Other' category
  return false;
};

/**
 * Convert File to base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export default scanReceipt;